import React, { useState } from 'react';
import { jsPDF } from 'jspdf'; // Import jsPDF for PDF generation
import './CustomGameCreator.css';

const CustomGameCreator = () => {
  const [formData, setFormData] = useState({
    clubName: '',
    gameName: '', // New field for game name
    topSealPrize: '',
    smallerPayouts: [{ description: '', count: '' }], // Initialize with one empty entry
    ticketCount: '', // New field for ticket count
    payout: '', // New field for payout
    gamesPerCase: '',
    customImage: null,
  });

  const [selectedTemplate, setSelectedTemplate] = useState('template1'); // Default template

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handlePayoutChange = (index, field, value) => {
    const updatedPayouts = [...formData.smallerPayouts];
    updatedPayouts[index][field] = value;
    setFormData({ ...formData, smallerPayouts: updatedPayouts });
  };

  const addPayoutField = () => {
    setFormData({
      ...formData,
      smallerPayouts: [...formData.smallerPayouts, { description: '', count: '' }],
    });
  };

  const removePayoutField = (index) => {
    const updatedPayouts = formData.smallerPayouts.filter((_, i) => i !== index);
    setFormData({ ...formData, smallerPayouts: updatedPayouts });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setFormData({ ...formData, customImage: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleTemplateChange = (template) => {
    setSelectedTemplate(template); // Update the selected template
  };

  const downloadPDF = () => {
    const doc = new jsPDF();

    // Add content to the PDF
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text(formData.clubName || 'Your Club Name', 10, 20);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(14);
    doc.text(`Game Name: ${formData.gameName || 'Your Game Name'}`, 10, 30);
    doc.text(`Top Seal Prize: ${formData.topSealPrize || 'Enter a prize'}`, 10, 40);

    doc.text('Smaller Payouts:', 10, 50);
    formData.smallerPayouts.forEach((payout, index) => {
      doc.text(
        `${index + 1}. ${payout.description || 'Enter payout'} - ${payout.count || 'Enter count'} winners`,
        10,
        60 + index * 10
      );
    });

    doc.text(`Ticket Count: ${formData.ticketCount || 'Enter count'}`, 10, 70 + formData.smallerPayouts.length * 10);
    doc.text(`Payout: ${formData.payout || 'Enter payout'}`, 10, 80 + formData.smallerPayouts.length * 10);
    doc.text(`Games Per Case: ${formData.gamesPerCase || 'Enter number'}`, 10, 90 + formData.smallerPayouts.length * 10);

    // Add the club logo if available
    if (formData.customImage) {
      doc.addImage(formData.customImage, 'JPEG', 150, 10, 40, 40); // Position and size of the image
    }

    // Save the PDF
    doc.save(`${formData.clubName || 'CustomGamePoster'}.pdf`);
  };

  return (
    <div className="custom-game-shell">
      <div className="custom-game-container">
        <div className="custom-form-panel">
          <h2 className="custom-game-title">Build Your Custom Game</h2>
          <form className="custom-form">
          <label className="custom-form-label">
            Club Name:
            <input
              type="text"
              name="clubName"
              value={formData.clubName}
              onChange={handleInputChange}
              placeholder="Enter your club name"
            />
          </label>
          <label className="custom-form-label">
            Game Name:
            <input
              type="text"
              name="gameName"
              value={formData.gameName}
              onChange={handleInputChange}
              placeholder="Enter your game name"
            />
          </label>
          <label className="custom-form-label">
            Top Seal Prize:
            <input
              type="text"
              name="topSealPrize"
              value={formData.topSealPrize}
              onChange={handleInputChange}
              placeholder="Enter top seal prize"
            />
          </label>
          <label className="custom-form-label">
            Smaller Payouts:
            {formData.smallerPayouts.map((payout, index) => (
              <div key={index} className="custom-payout-field">
                <input
                  type="text"
                  value={payout.description}
                  onChange={(e) => handlePayoutChange(index, 'description', e.target.value)}
                  placeholder={`Payout ${index + 1} Description`}
                />
                <input
                  type="number"
                  value={payout.count}
                  onChange={(e) => handlePayoutChange(index, 'count', e.target.value)}
                  placeholder="Count"
                  className="custom-payout-count"
                />
                <button
                  type="button"
                  className="custom-remove-payout-btn"
                  onClick={() => removePayoutField(index)}
                  disabled={formData.smallerPayouts.length === 1}
                >
                  Remove
                </button>
              </div>
            ))}
            <button type="button" className="custom-add-payout-btn" onClick={addPayoutField}>
              + Add Payout
            </button>
          </label>
          <label className="custom-form-label">
            Ticket Count/Take In:
            <input
              type="number"
              name="ticketCount"
              value={formData.ticketCount}
              onChange={handleInputChange}
              placeholder="Enter ticket count"
            />
          </label>
          <label className="custom-form-label">
            Payout:
            <input
              type="text"
              name="payout"
              value={formData.payout}
              onChange={handleInputChange}
              placeholder="Enter payout amount"
            />
          </label>
          <label className="custom-form-label">
            Games Per Case:
            <input
              type="number"
              name="gamesPerCase"
              value={formData.gamesPerCase}
              onChange={handleInputChange}
              placeholder="Enter number of games per case"
            />
          </label>
          <label className="custom-form-label">
            Upload Club Logo:
            <input type="file" onChange={handleImageUpload} />
          </label>
        </form>
        <div className="custom-template-buttons">
          <button
            type="button"
            className={`custom-template-btn ${selectedTemplate === 'template1' ? 'active' : ''}`}
            onClick={() => handleTemplateChange('template1')}
          >
            Classic Theme
          </button>
          <button
            type="button"
            className={`custom-template-btn ${selectedTemplate === 'template2' ? 'active' : ''}`}
            onClick={() => handleTemplateChange('template2')}
          >
            Modern Theme
          </button>
          <button
            type="button"
            className={`custom-template-btn ${selectedTemplate === 'template3' ? 'active' : ''}`}
            onClick={() => handleTemplateChange('template3')}
          >
            Fun Theme
          </button>
        </div>
      </div>

      <div className="custom-preview-panel">
        <h2 className="custom-preview-title">Game Poster Preview</h2>
        <div className={`custom-game-card ${selectedTemplate}`}>
          {formData.customImage && (
            <img src={formData.customImage} alt="Club Logo" className="custom-club-logo" />
          )}
          <h3>{formData.clubName || 'Your Club Name'}</h3>
          <h4>{formData.gameName || 'Your Game Name'}</h4>
          <p><strong>Top Seal Prize:</strong> {formData.topSealPrize || 'Enter a prize'}</p>
          <p><strong>Smaller Payouts/Instant Wins:</strong></p>
          <div className="custom-payouts-column">
            {formData.smallerPayouts.map((payout, index) => (
              <div key={index} className="custom-payout-item">
                <span>{payout.description || 'Enter payout'}</span>
                <span>{payout.count || 'Enter count'} winners</span>
              </div>
            ))}
          </div>
          <p><strong>Ticket Count:</strong> {formData.ticketCount || 'Enter count'}</p>
          <p><strong>Payout:</strong> {formData.payout || 'Enter payout'}</p>
          <p><strong>Games Per Case:</strong> {formData.gamesPerCase || 'Enter number'}</p>
        </div>
        <button className="custom-download-pdf-btn" onClick={downloadPDF}>
          Download Poster as PDF
        </button>
        <p className="custom-email-instructions">
          To create your custom game, download the poster with your game specs and email it
          <a href="mailto:skhan139@icloud.com"> here</a>. We will get back to you to start the process.
        </p>
      </div>
    </div>
    </div>
  );
};

export default CustomGameCreator;